declare global {
    interface Window {
        fbq?: (...args: any[]) => void;
    }
}

let initialized = false;

export function initMetaPixel(metaPixelId: string) {
    if (initialized || !metaPixelId) {
        return;
    }

    console.log("Initializing Meta Pixel:", metaPixelId);

    const script = document.createElement("script");
    script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${metaPixelId}');
    fbq('track', 'PageView');
    `;
    document.head.prepend(script);

    const noScript = document.createElement("noscript");
    noScript.innerHTML = `<img height="1" width="1" style="display:none" alt="" src="https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1"/>`;
    document.body.prepend(noScript);

    initialized = true;
}

// ── Eventos del embudo de compra ────────────────────────────────────────────
//
// Regla general: `content_ids` siempre usa el id de la VARIANTE (nunca el del
// producto), porque tiene que matchear con el `g:id` del feed de catálogo de
// Meta: es lo que efectivamente se agrega al carrito, se compra y tiene
// precio/stock propio.

export interface MetaPixelContentItem {
    id: string;
    quantity: number;
}

function isFbqAvailable(): boolean {
    return typeof window !== "undefined" && typeof window.fbq === "function";
}

export function trackViewContent(variantId: string, price: number, currency = "ARS"): void {
    if (!isFbqAvailable() || !variantId) return;

    window.fbq!("track", "ViewContent", {
        content_ids: [variantId],
        content_type: "product",
        value: price,
        currency,
    });
}

/**
 * No es un evento estándar de Meta (no existe uno oficial para "ver un
 * listado"), por eso va como trackCustom. No alimenta optimización automática
 * de Meta ni catálogo dinámico: sirve para reporting y audiencias manuales.
 */
export function trackViewCategory(categoryName: string): void {
    if (!isFbqAvailable() || !categoryName) return;

    window.fbq!("trackCustom", "ViewCategory", {
        content_category: categoryName,
    });
}

export function trackAddToCart(variantId: string, price: number, currency = "ARS"): void {
    if (!isFbqAvailable() || !variantId) return;

    window.fbq!("track", "AddToCart", {
        content_ids: [variantId],
        content_type: "product",
        value: price,
        currency,
    });
}

export function trackInitiateCheckout(
    items: MetaPixelContentItem[],
    total: number,
    currency = "ARS",
): void {
    if (!isFbqAvailable() || items.length === 0) return;

    window.fbq!("track", "InitiateCheckout", {
        content_ids: items.map((item) => item.id),
        contents: items.map((item) => ({ id: item.id, quantity: item.quantity })),
        currency,
        num_items: items.length,
        value: total,
    });
}

const PURCHASE_TRACKED_STORAGE_KEY = "@ecommerce/analytics:pixel-purchases-tracked";
const MAX_TRACKED_PURCHASE_IDS = 50;

function readTrackedPurchaseIds(): string[] {
    try {
        const raw = localStorage.getItem(PURCHASE_TRACKED_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function rememberTrackedPurchaseId(orderId: string): void {
    try {
        const ids = readTrackedPurchaseIds().filter((id) => id !== orderId);
        ids.push(orderId);
        const trimmed = ids.slice(-MAX_TRACKED_PURCHASE_IDS);
        localStorage.setItem(PURCHASE_TRACKED_STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
        // localStorage no disponible (modo privado, storage lleno, etc.): no bloquea el tracking.
    }
}

/**
 * Dispara Purchase una única vez por orden: si el usuario refresca la página
 * de gracias, `orderId` ya está en el registro local y no se re-dispara.
 */
export function trackPurchase(
    orderId: string,
    items: MetaPixelContentItem[],
    total: number,
    currency = "ARS",
): void {
    if (!isFbqAvailable() || !orderId || items.length === 0) return;
    if (readTrackedPurchaseIds().includes(orderId)) return;

    window.fbq!("track", "Purchase", {
        content_ids: items.map((item) => item.id),
        content_type: "product",
        contents: items.map((item) => ({ id: item.id, quantity: item.quantity })),
        currency,
        value: total,
    });

    rememberTrackedPurchaseId(orderId);
}
